import { useCallback, useEffect, useRef, useState } from "react";

// Enregistreur de note vocale (MediaRecorder). WKWebView/Safari produisent du
// `audio/mp4`, Chromium du `audio/webm` ; on choisit le 1er type supporté et on
// nomme le fichier en conséquence. À l'arrêt « envoyer », on rappelle onComplete
// avec un File prêt à téléverser ; « annuler » jette l'enregistrement.
function pickMime(): { mime: string; ext: string } {
  const candidates: [string, string][] = [
    ["audio/webm;codecs=opus", "webm"],
    ["audio/ogg;codecs=opus", "ogg"],
    ["audio/webm", "webm"],
    ["audio/mp4", "m4a"],
  ];
  for (const [mime, ext] of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(mime)) {
      return { mime, ext };
    }
  }
  return { mime: "", ext: "webm" };
}

export interface VoiceRecorder {
  recording: boolean;
  seconds: number;
  error: string | null;
  /** true si l'échec vient d'un refus d'accès micro (→ proposer les Réglages). */
  permissionDenied: boolean;
  start: () => Promise<void>;
  stop: () => void; // finalise → onComplete(file)
  cancel: () => void; // jette l'enregistrement
  dismissError: () => void;
}

export function useVoiceRecorder(onComplete: (file: File) => void): VoiceRecorder {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const sendOnStopRef = useRef(false);
  const extRef = useRef("webm");
  // onComplete via ref : le callback peut changer (replyTo, etc.) entre start et
  // stop ; on appelle toujours la dernière version sans recréer `start`.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setSeconds(0);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setPermissionDenied(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { mime, ext } = pickMime();
      extRef.current = ext;
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const send = sendOnStopRef.current;
        cleanup();
        if (send && blob.size > 0) {
          onCompleteRef.current(new File([blob], `note-vocale.${extRef.current}`, { type }));
        }
      };
      rec.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      // NotAllowedError = l'utilisateur (ou le système) a refusé le micro → on
      // propose d'ouvrir les Réglages ; les autres erreurs = micro absent/occupé.
      const denied = err instanceof DOMException && err.name === "NotAllowedError";
      setPermissionDenied(denied);
      setError(
        denied
          ? "Accès au micro refusé. Autorise « Agency OS » dans Réglages › Confidentialité › Microphone."
          : "Micro indisponible. Vérifie qu'un micro est branché et libre.",
      );
      cleanup();
    }
  }, [cleanup]);

  const finish = useCallback(
    (send: boolean) => {
      const rec = recRef.current;
      sendOnStopRef.current = send;
      if (rec && rec.state !== "inactive") rec.stop();
      else cleanup();
    },
    [cleanup],
  );

  return {
    recording,
    seconds,
    error,
    permissionDenied,
    start,
    stop: () => finish(true),
    cancel: () => finish(false),
    dismissError: () => {
      setError(null);
      setPermissionDenied(false);
    },
  };
}
